<template>
    <div class="page"
        data-loaded="false" :id="pageContainerId"
        data-page-number="pageNumber"
        @mousedown="startDrag" @touchstart="startDrag"
        @mousemove="drag" @touchmove="drag"
        @mouseup="endDrag" @touchend="endDrag"
        @mouseout="stopDrag"
        v-on:dblclick="handleDoubleClick"
        v-longpress="longPressHandler"
        @contextmenu="preventContext($event)"
    >
        <div class="canvasWrapper" :class="{'hide-text-layer': isTextLayerHidden()}" @focusin="onCanvasFocus" @focusout="onCanvasBlur">
            <canvas :id="`page${pageNumber}`" tabindex="0" :height="pageHeight" :width="pageWidth"></canvas>
        </div>
        <div class="textLayer" :class="{'hide-text-layer': isTextLayerHidden()}"></div>
    </div>
</template>

<script>
// Vendor
import Utils from '@/lib/Utils';
import { PDFJS } from 'pdfjs-dist/web/pdf_viewer';
import { mapGetters } from 'vuex';
import VueClipboard from 'vue-clipboard2';
import Vue from 'vue';

Vue.use(VueClipboard);

// Component
PDFJS.workerSrc = '../../../node_modules/pdfjs-dist/build/pdf.worker.js';

const ProjectPdfPage = {
    name: 'ProjectPdfPage',
    props: ['page', 'pageWidth', 'pageHeight', 'currentCueLayer', 'issrc', 'locked', 'positionedCues', 'svgTrueHeight', 'selectedElement', 'openCues'],
    data: function() {
        return {
            pdfRender: null,
            pageNumber: this.$props.page.pageNumber,
            pdfPage: this.$props.page.pdfPage,
            pageContainerId: 'pageContainer' + this.$props.page.pageNumber,
            ctx: null,
            canvas: null,
            overlayCanvas: null,
            overlayCtx: null,
            isDragging: false,
            isDrawing: false,
            isDraggingTime: {},
            initialX: 0,
            initialY: 0,
            xOffset: 0,
            yOffset: 0,
            x: 0,
            y: 0,
            clickCount: 0,
            offsetX: 0,
            offsetY: 0,
            drawShape: [],
            lineWidth: 2,
            isCanvasFocused: false,
            timeToHold: 500,
            lastTouchTime: 0,
            cueSelected: false
        };
    },
    computed: {
        ...mapGetters([
            'user',
            'selectedTool',
            'selectedToolIndex',
            'selectedScale',
            'currentLayer',
            'currentNoteLayer',
            'showNotesLayerIDs',
            'showCuesLayerIDs',
            'visibleCues',
            'project'
        ]),
        isIpad() {
            return /iPad|Macintosh/i.test(navigator.userAgent) && 'ontouchend' in document;
        }
    },
    watch: {
        selectedScale: function(newScale, oldScale) {
            if (newScale !== oldScale) {
                this.renderPage();
            }
        }
    },
    methods: {
        renderPage: function() {
            const canvas = this.$el.querySelector('.canvasWrapper canvas');

            this.canvas = this.$el.querySelector('.canvasWrapper canvas');

            const canvasContext = canvas.getContext('2d', {alpha: false});
            this.ctx = canvasContext;
            this.w = this.canvas.width;
            this.h = this.canvas.height;

            const canvasOffset = this.canvas.getBoundingClientRect();
            this.offsetX = canvasOffset.left;
            this.offsetY = canvasOffset.top;

            const viewport = Utils.getViewport(this.pdfPage, this.w, this.h, this.selectedScale);

            const transform = Utils.scalePage(this.$el, viewport, canvasContext);
            this.pdfRender = this.pdfPage.render({ canvasContext, viewport, transform })
            .then(() => {
                // Indicate that the page was loaded
                this.$el.dataset.loaded = 'true';
                return this.pdfPage.getTextContent({normalizeWhitespace: true})
                .then((textContent) => {
                    const textLayer = this.$el.querySelector('.textLayer');
                    textLayer.innerHTML = '';
                    const textLayerFactory = new PDFJS.DefaultTextLayerFactory();
                    const textLayerBuilder = textLayerFactory.createTextLayerBuilder(
                        textLayer, this.pageNumber, viewport, true);
                    textLayerBuilder.setTextContent(textContent);
                    
                    const newFontScale = 15 * this.selectedScale;
                    document.documentElement.style.setProperty('--cue-marker-font-size', `${newFontScale}px`);

                    textLayerBuilder.render();
                    this.$emit('pdfLoaded',
                        {pageText: textContent, pageNumber: this.pageNumber, viewport: viewport});
                });
            });
        },
        isTextLayerHidden() {
            return this.selectedTool === 'cueSelect' || this.selectedTool === 'free_drawing' || this.selectedTool === 'highlight' || this.selectedTool === 'stamp';
        },
        handleMouseDown(e) {
            e.preventDefault();
            e.stopPropagation();
        },
        handleMouseUp(e) {
            e.preventDefault();
            e.stopPropagation();
        },
        handleMouseOut(e) {
            e.preventDefault();
            e.stopPropagation();
        },
        handleMouseMove() {

        },
        onCanvasFocus() {
            this.isCanvasFocused = true;
        },
        onCanvasBlur() {
            this.isCanvasFocused = false;
        },
        pasteCueNote(event) {
            if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
                this.$nextTick(() => {
                    const canvas = this.$el.querySelector('.canvasWrapper canvas');
                    const bounds = canvas.getBoundingClientRect();
                    const noteText = {
                        ...this.selectedElement,
                        historyType: 'update',
                        text: this.selectedElement.text,
                        subType: this.selectedElement.subType,
                        toText: this.selectedElement.text
                    };
                    this.updatePageHistory(noteText);
                    const note = {
                        width: this.selectedElement.width,
                        height: this.selectedElement.height,
                        x: this.initialX - bounds.x,
                        y: this.initialY - bounds.y,
                        text: this.selectedElement.text,
                        type: this.selectedElement.type,
                        subType: this.selectedElement.subType,
                        pageNumber: this.selectedElement.page
                    };
                    this.$store.dispatch('addUserNote', {
                        note,
                        layer: this.currentNoteLayer
                    }).then((noteData) => {
                        if (noteData.type !== 'text' && noteData.type !== 'stamp') return;
                        setTimeout(() => {
                            const noteRef = document.querySelector('#' + noteData.type + '-note-' + noteData.id);
                            noteRef.parentElement.click();
                            noteRef.focus();
                        }, 100);
                    });
                });
            }
        },
        // Function to save the current canvas state
        saveCanvasState() {
            this.imageData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
        },
        // Function to restore the saved canvas state
        restoreCanvasState() {
            if (this.imageData) {
                this.ctx.putImageData(this.imageData, 0, 0);
            }
        },
        createSVGPath(coordinates) {
            const svgNamespace = 'http://www.w3.org/2000/svg';
            const svgPath = document.createElementNS(svgNamespace, 'path');

            // Construct the path data string
            let pathData = `M ${coordinates[0].prevX} ${coordinates[0].prevY}`;
            let prevPoints = {
                prevX: coordinates[0].prevX,
                prevY: coordinates[0].prevY
            };
            for (const point of coordinates) {
                if (Number.parseFloat(prevPoints.prevX) === Number.parseFloat(point.prevX) && Number.parseFloat(prevPoints.prevY) === Number.parseFloat(point.prevY)) {
                    pathData += ` L ${point.currX} ${point.currY}`;
                } else {
                    pathData += ` M ${point.currX} ${point.currY}`;
                }
                prevPoints = {
                    prevX: point.currX,
                    prevY: point.currY
                };
            }

            // Set the path attributes
            svgPath.setAttribute('d', pathData);
            svgPath.setAttribute('stroke', 'black'); // Example stroke color
            svgPath.setAttribute('stroke-width', this.currentNoteLayer.lineThikness); // Example stroke width
            svgPath.setAttribute('fill', 'none'); // No fill

            return svgPath;
        },
        // Function to create SVG path string from coordinates
        createSVGPathString(coordinates, svgWidth, svgHeight) {
            let pathData = `M ${coordinates[0].prevX} ${coordinates[0].prevY}`;
            let prevPoints = {
                prevX: coordinates[0].prevX,
                prevY: coordinates[0].prevY
            };
            for (const point of coordinates) {
                if (Number.parseFloat(prevPoints.prevX) === Number.parseFloat(point.prevX) && Number.parseFloat(prevPoints.prevY) === Number.parseFloat(point.prevY)) {
                    pathData += ` L ${point.currX} ${point.currY}`;
                } else {
                    pathData += ` M ${point.currX} ${point.currY}`;
                }
                prevPoints = {
                    prevX: point.currX,
                    prevY: point.currY
                };
            }

            const invScale = 1 / this.selectedScale;
            const padding = Math.max(20, (this.currentNoteLayer.lineThikness || 0) * 4);
            const svgWH = {
                // NOTE: coordinates are stored in "unscaled" space (we multiply by invScale when building them),
                // so the SVG dimensions must also be in unscaled space; otherwise the path will clip at scale < 1.
                width: svgWidth || (((this.drawShapeMeta.drawShapeXe - this.drawShapeMeta.drawShapeX) * invScale) + padding),
                height: svgHeight || (((this.drawShapeMeta.drawShapeYe - this.drawShapeMeta.drawShapeY) * invScale) + padding)
            };

            const svgString = `<svg class="draw-svg" width="${svgWH.width}" height="${svgWH.height}" xmlns="http://www.w3.org/2000/svg">
                <path d="${pathData}" stroke="${this.currentNoteLayer.primaryColor}" stroke-width="${this.currentNoteLayer.lineThikness}" fill="none"/>
            </svg>`;
            return svgString;
        },
        // Point the live free-drawing stroke at a transparent overlay canvas
        // that page.vue renders above the annotation layers, so the stroke is
        // visible over images/notes as it is drawn (instead of being painted
        // onto the PDF canvas underneath them). The overlay's backing store is
        // matched to the PDF canvas so the existing draw math lines up exactly.
        prepareDrawOverlay() {
            const overlay = document.getElementById('ql-drawing-overlay-' + this.pageNumber);
            if (!overlay || !this.canvas) {
                this.overlayCanvas = null;
                this.overlayCtx = null;
                return;
            }
            overlay.width = this.canvas.width;
            overlay.height = this.canvas.height;
            this.overlayCanvas = overlay;
            this.overlayCtx = overlay.getContext('2d');
            this.overlayCtx.clearRect(0, 0, overlay.width, overlay.height);
        },
        clearDrawOverlay() {
            if (this.overlayCtx && this.overlayCanvas) {
                this.overlayCtx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
            }
        },
        drawFinalShape() {
            this.isDragging = false;
            this.isDrawing = false;

            if (this.drawShape.length > 1) {
                this.drawShapeTmp = this.drawShape;
                this.drawShape = [];
                const newDrawShape = [];

                // Recalculate bounding box from all points to ensure accuracy
                let minX = Infinity;
                let minY = Infinity;
                let maxX = -Infinity;
                let maxY = -Infinity;
                for (const coords of this.drawShapeTmp) {
                    minX = Math.min(minX, coords.prevX, coords.currX);
                    minY = Math.min(minY, coords.prevY, coords.currY);
                    maxX = Math.max(maxX, coords.prevX, coords.currX);
                    maxY = Math.max(maxY, coords.prevY, coords.currY);
                }
                
                // Update drawShapeMeta with the recalculated bounding box
                this.drawShapeMeta = {
                    drawShapeX: minX,
                    drawShapeY: minY,
                    drawShapeXe: maxX,
                    drawShapeYe: maxY
                };

                const invScale = 1 / this.selectedScale;
                const padding = Math.max(20, (this.currentNoteLayer.lineThikness || 0) * 4);
                const offset = padding / 2;
                const svgWidth = ((this.drawShapeMeta.drawShapeXe - this.drawShapeMeta.drawShapeX) * invScale) + padding;
                const svgHeight = ((this.drawShapeMeta.drawShapeYe - this.drawShapeMeta.drawShapeY) * invScale) + padding;

                for (const coords of this.drawShapeTmp) {
                    const newCords = {
                        currX: ((coords.currX - this.drawShapeMeta.drawShapeX) * invScale) + offset,
                        currY: ((coords.currY - this.drawShapeMeta.drawShapeY) * invScale) + offset,
                        prevX: ((coords.prevX - this.drawShapeMeta.drawShapeX) * invScale) + offset,
                        prevY: ((coords.prevY - this.drawShapeMeta.drawShapeY) * invScale) + offset
                    };
                    newDrawShape.push(newCords);
                }

                if (newDrawShape.length > 0) {
                    // Create the SVG path element
                    const svgPathElement = this.createSVGPath(newDrawShape);

                    // Create an SVG container if you need one
                    const svgContainer = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                    svgContainer.setAttribute('width', svgWidth);
                    svgContainer.setAttribute('height', svgHeight);

                    // Append the path to the SVG container
                    svgContainer.appendChild(svgPathElement);
                    const strText = this.createSVGPathString(newDrawShape, svgWidth, svgHeight);
                    const offsetScreen = offset * this.selectedScale;
                    const data = {
                        // Keep the note container dimensions consistent with the SVG dimensions to prevent clipping.
                        width: svgWidth,
                        height: svgHeight,
                        x: (this.drawShapeMeta.drawShapeX - offsetScreen) / this.pageWidth,
                        opacity: this.currentNoteLayer.lineThikness,
                        color: this.currentNoteLayer.primaryColor,
                        y: (this.drawShapeMeta.drawShapeY - offsetScreen) / this.pageHeight,
                        metadata: this.drawShapeMeta,
                        text: strText,
                        coords: newDrawShape
                    };

                    this.$emit('addNote', {
                        data,
                        type: 'free_drawing'
                    });
                    this.restoreCanvasState();
                }
            }
            // The finalized note now renders the stroke; wipe the live overlay.
            this.clearDrawOverlay();
        },
        findxy(res, e) {
            const bounds = this.canvas.getBoundingClientRect();
            if (res === 'down') {
                clearTimeout(this.isDraggingTime);

                this.prevX = this.currX;
                this.prevY = this.currY;

                if (e.touches !== undefined && e.touches.length === 1) {
                    this.currX = e.touches[0].clientX - bounds.x - this.canvas.offsetLeft;
                    this.currY = e.touches[0].clientY - bounds.y - this.canvas.offsetTop;
                } else {
                    this.currX = e.clientX - bounds.x - this.canvas.offsetLeft;
                    this.currY = e.clientY - bounds.y - this.canvas.offsetTop;
                }

                if (!this.isDrawing) {
                    this.drawShape = [];
                    this.saveCanvasState();
                    this.prepareDrawOverlay();
                }

                if (!this.drawShapeMeta || !this.isDrawing) {
                    this.drawShapeMeta = {
                        drawShapeX: this.currX,
                        drawShapeY: this.currY,
                        drawShapeXe: 0,
                        drawShapeYe: 0
                    };
                }

                this.isDrawing = true;
                this.isDragging = true;
            }
            if (res === 'up' || res === 'out') {
                if (this.isDragging) {
                    // Add the final point to drawShape before finalizing
                    let finalX;
                    let finalY;
                    
                    // Handle touch events (check changedTouches first for touchend events)
                    if (e.changedTouches !== undefined && e.changedTouches.length > 0) {
                        finalX = e.changedTouches[0].clientX - bounds.x - this.canvas.offsetLeft;
                        finalY = e.changedTouches[0].clientY - bounds.y - this.canvas.offsetTop;
                    } else if (e.touches !== undefined && e.touches.length === 1) {
                        finalX = e.touches[0].clientX - bounds.x - this.canvas.offsetLeft;
                        finalY = e.touches[0].clientY - bounds.y - this.canvas.offsetTop;
                    } else {
                        // Mouse event
                        finalX = e.clientX - bounds.x - this.canvas.offsetLeft;
                        finalY = e.clientY - bounds.y - this.canvas.offsetTop;
                    }
                    
                    // Only add if the final point is different from the last point
                    if (this.drawShape.length === 0 || 
                        this.drawShape[this.drawShape.length - 1].currX !== finalX || 
                        this.drawShape[this.drawShape.length - 1].currY !== finalY) {
                        this.drawShape.push({
                            prevX: this.currX,
                            prevY: this.currY,
                            currX: finalX,
                            currY: finalY
                        });
                        
                        // Update bounding box to include the final point
                        this.drawShapeMeta = {
                            drawShapeX: Math.min(this.currX, finalX, this.drawShapeMeta.drawShapeX),
                            drawShapeY: Math.min(this.currY, finalY, this.drawShapeMeta.drawShapeY),
                            drawShapeXe: Math.max(this.currX, finalX, this.drawShapeMeta.drawShapeXe),
                            drawShapeYe: Math.max(this.currY, finalY, this.drawShapeMeta.drawShapeYe)
                        };
                    }
                }
                this.isDragging = false;
                this.isDraggingTime = setTimeout(this.drawFinalShape, this.timeToHold);
            } 
            if (res === 'move') {
                if (this.isDragging) {
                    this.prevX = this.currX;
                    this.prevY = this.currY;
                    
                    if (e.touches !== undefined && e.touches.length === 1) {
                        this.currX = e.touches[0].clientX - bounds.x - this.canvas.offsetLeft;
                        this.currY = e.touches[0].clientY - bounds.y - this.canvas.offsetTop;
                    } else {
                        this.currX = e.clientX - bounds.x - this.canvas.offsetLeft;
                        this.currY = e.clientY - bounds.y - this.canvas.offsetTop;
                    }

                    this.drawShape.push(
                        {
                            prevX: this.prevX,
                            prevY: this.prevY,
                            currX: this.currX,
                            currY: this.currY
                        }
                    );

                    this.drawShapeMeta = {
                        drawShapeX: Math.min(this.prevX, this.currX, this.drawShapeMeta.drawShapeX),
                        drawShapeY: Math.min(this.prevY, this.currY, this.drawShapeMeta.drawShapeY),
                        drawShapeXe: Math.max(this.prevX, this.currX, this.drawShapeMeta.drawShapeXe),
                        drawShapeYe: Math.max(this.prevY, this.currY, this.drawShapeMeta.drawShapeYe)
                    };

                    this.draw();
                }
            }
        },
        draw() {
            // Free-drawing renders onto the transparent overlay (above the
            // annotation layers); other tools keep drawing on the PDF canvas.
            const ctx = (this.selectedTool === 'free_drawing' && this.overlayCtx) ? this.overlayCtx : this.ctx;
            ctx.beginPath();
            ctx.moveTo(this.prevX * window.devicePixelRatio, this.prevY * window.devicePixelRatio);
            ctx.lineTo(this.currX * window.devicePixelRatio, this.currY * window.devicePixelRatio);
            ctx.strokeStyle = this.currentNoteLayer.primaryColor;
            if (this.selectedTool === 'free_drawing') {
                ctx.lineWidth = this.currentNoteLayer.lineThikness * this.selectedScale;
            } else {
                ctx.lineWidth = this.lineWidth * this.selectedScale;
            }
            ctx.stroke();
            ctx.closePath();
        },
        startDrag(event) {
            if (!this.project.isSample) {
                if (!this.user.active_pay) {
                    if (!this.issrc && !this.project.unlocked) {
                        this.$emit('openDialog', 'no-collaborator-dialog');
                        return;
                    }
                }
            }
            if (this.selectedTool === 'cueSelect') {
                if (!this.currentCueLayer || (this.currentCueLayer && !this.showCuesLayerIDs.includes(this.currentCueLayer.id))) {
                    this.$emit('openDialog', 'no-layers-dialog');
                    return;
                }
            } 
            if ((this.selectedTool !== 'cueSelect' && !this.currentNoteLayer) || (this.currentNoteLayer && !this.showNotesLayerIDs.includes(this.currentNoteLayer.id))) {
                this.$emit('openDialog', 'no-layers-dialog');
                return;
            }
            
            if (this.locked) return;
            if (event.touches !== undefined && event.touches.length === 1) {
                this.isDragging = true;
                this.initialX = event.touches[0].clientX;
                this.initialY = event.touches[0].clientY;
            } else if (!this.isIpad) {
                this.isDragging = true;
                this.initialX = event.clientX;
                this.initialY = event.clientY;
            }
            this.disableNoteContainer(true);
            
            if (this.selectedTool === 'free_drawing') {
                this.findxy('down', event);
            } else {
                this.saveCanvasState();
            }
        },
        handleDoubleClick(event) {
            if (!this.isIpad) {
                this.doubleClicked(event);
            }
        },
        isTouchDevice() {
            return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        },
        longPressHandler(event) {
            const eventV = event.touches ? event.touches[0] : event;
            if (this.selectedTool === 'cueSelect' || this.selectedTool === 'highlight') {
                if (!this.cueSelected) {
                    this.doubleClicked(eventV);
                }
            } else {
                this.doubleClicked(eventV);
            }            
        },
        disableNoteContainer(trigger) {
            const qlp = document.getElementsByClassName('ql-project-page');
            const qlcl = document.getElementsByClassName('ql-cue-list')[0];

            if (qlp.length > 0) { // Check if there are any elements with the class
                for (const element of qlp) {
                    if (trigger) {
                        element.classList.add('disable-note-container');
                    } else {
                        element.classList.remove('disable-note-container');
                    }
                }
            }

            if (qlcl) { // Check if the element exists
                if (trigger) {                    
                    qlcl.classList.add('disable-note-container');
                } else {
                    qlcl.classList.remove('disable-note-container');
                }
            }
        },
        drag(e) {
            if (e.touches !== undefined && e.touches.length === 1 && this.selectedTool !== 'pointerSelect') {
                e.preventDefault();
                e = e.touches[0];
                const moveX = e.clientX - this.initialX;
                const moveY = e.clientY - this.initialY;
                if (Math.abs(moveX) > 2 || Math.abs(moveY) > 2) {
                    this.cueSelected = true;
                }
            }
            if (this.isDragging && (this.selectedTool === 'cueSelect' || this.selectedTool === 'highlight')) {                
                this.xOffset = e.clientX - this.initialX;
                this.yOffset = e.clientY - this.initialY;
                this.x = this.xOffset;
                this.y = this.yOffset;

                const canvas = this.$el.querySelector('.canvasWrapper canvas');
                const ctx = canvas.getContext('2d');
                ctx.strokeStyle = 'black';
                ctx.lineWidth = 1;
                ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';

                const bounds = canvas.getBoundingClientRect();

                const originX = Math.min(this.initialX, e.clientX) - bounds.x;
                const originY = Math.min(this.initialY, e.clientY) - bounds.y;

                const initWidth = Math.abs((e.clientX - bounds.x - 30) - (this.initialX - bounds.x - 30));
                const initHeight = Math.abs((e.clientY - bounds.y - 10) - (this.initialY - bounds.y - 10));

                this.restoreCanvasState();

                ctx.fillRect(originX * window.devicePixelRatio, originY * window.devicePixelRatio, initWidth * window.devicePixelRatio, initHeight * window.devicePixelRatio);
                ctx.strokeRect(originX * window.devicePixelRatio, originY * window.devicePixelRatio, initWidth * window.devicePixelRatio, initHeight * window.devicePixelRatio);
            }

            if (this.selectedTool === 'free_drawing') {               
                this.findxy('move', e);
            }
        },
        endDrag(event) {
            this.disableNoteContainer(false);
            if (this.isDragging && this.initialX !== event.clientX && this.initialY !== event.clientY) {
                if ((event.type === 'touchend' || event.button === 0) && this.selectedTool === 'cueSelect') {
                    this.clickCount++;
                    setTimeout(() => {
                        this.restoreCanvasState();
                        if (this.clickCount === 1) {
                            this.cueSelected = false;
                            this.clickCount = 0;
                            if (event.type === 'touchend' && event.changedTouches.length === 1) {
                                const moveX = event.changedTouches[0].clientX - this.initialX;
                                const moveY = event.changedTouches[0].clientY - this.initialY;
                                if (Math.abs(moveX) > 2 || Math.abs(moveY) > 2) {
                                    if (!this.project.isSample) {
                                        if (!this.user.active_pay) {
                                            if (!this.issrc && !this.project.unlocked) {
                                                this.$emit('openDialog', 'no-collaborator-dialog');
                                                return;
                                            }
                                        }
                                    }
                                    this.initialX = event.changedTouches[0].clientX - this.xOffset;
                                    this.initialY = event.changedTouches[0].clientY - this.yOffset;
                                    this.createSelectionCue(event);
                                    this.isDragging = false;
                                    event.preventDefault();
                                }
                            } else if (event.changedTouches) {
                                // A touch that ended without one clean finger lift (two fingers, a cancel): it has no
                                // mouse position, and reading one made cues with no position (Josh, 28 Oct). No cue.
                                this.isDragging = false;
                            } else {
                                this.initialX = event.clientX - this.xOffset;
                                this.initialY = event.clientY - this.yOffset;
                                if (!this.project.isSample) {
                                    if (!this.user.active_pay) {
                                        if (!this.issrc && !this.project.unlocked) {
                                            this.$emit('openDialog', 'no-collaborator-dialog');
                                            return;
                                        }
                                    }
                                }
                                this.createSelectionCue(event);
                                this.isDragging = false;
                                event.preventDefault();
                            }
                        }
                        this.clickCount = 0;
                        this.isDragging = false;
                    }, this.timeToHold);
                } else if (this.selectedTool === 'highlight') {
                    if (event.type === 'touchend' && event.changedTouches.length === 1) {
                        const moveX = event.changedTouches[0].clientX - this.initialX;
                        const moveY = event.changedTouches[0].clientY - this.initialY;
                        if (Math.abs(moveX) > 2 || Math.abs(moveY) > 2) {
                            this.createHighlightNote(event);
                            this.isDragging = false;
                            event.preventDefault();
                        }
                    } else {
                        this.isDragging = false;
                        this.createHighlightNote(event);
                    }
                } else if (this.selectedTool !== 'free_drawing') {
                    setTimeout(() => {
                        this.restoreCanvasState();
                    }, this.timeToHold);
                }
            }

            if (this.selectedTool === 'free_drawing') {
                this.findxy('up', event);
            }
            this.isDragging = false;
        },
        stopDrag(event) {
            if (this.isDragging && this.selectedTool === 'free_drawing') {
                this.isDragging = false;
                this.findxy('out', event);
                this.restoreCanvasState();
            }
            this.endDrag(event);
        },
        copyElementContent() {
            if (this.selectedElement) {
                this.$data.copiedElement = this.selectedElement.cloneNode(true);
            }
        },
        doubleClicked: function(e) {
            if (!this.project.isSample) {
                if (!this.user.active_pay) {
                    if (!this.issrc && !this.project.unlocked) {
                        this.$emit('openDialog', 'no-collaborator-dialog');
                        return;
                    }
                }
            }
            switch (this.selectedTool) {
            case 'text':
                this.createTextNote(e);
                break;
            case 'highlight':
                this.createHighlightNote(e);
                break;
            case 'line':
                this.createLineNote(e);
                break;
            case 'stamp':
                this.createStampNote(e);
                break;
            case 'count':
                this.createCountNote(e);
                break;
            case 'image':
                this.createImage(e);
                break;
            case 'copy':
                this.copyElementContent(e);
                break;
            case 'cueSelect':
                this.createPositionCue(e);
                break;
            default:
                // do nothing
                break;
            }
        },
        mouseUp: function(e) {
            if (e.button === 2) {
                // do nothing
            }
        },
        updatePageHistory(data) {
            this.$emit('updatePageHistory', data);
        },
        createSelectionCuePlacement: function(e) {
            if (!this.project.isSample) {
                if (!this.user.active_pay) {
                    if (!this.issrc && !this.project.unlocked) {
                        this.$emit('openDialog', 'no-collaborator-dialog');
                        return;
                    }
                }
            }
            if (!this.currentCueLayer || (this.showCuesLayerIDs !== null && !this.showCuesLayerIDs.includes(this.currentCueLayer.id))) {
                this.$emit('openDialog', 'no-layers-dialog');
                return;
            }
            
            if (e.touches !== undefined && e.touches.length === 1) {
                e = e.touches[0];
            }

            if (this.locked) return;
            const canvas = this.$el.querySelector('.canvasWrapper canvas');
            const bounds = canvas.getBoundingClientRect();
            const cue = {
                type: 'position',
                width: 60,
                height: 20,
                x: e.clientX - bounds.x - 30,
                y: e.clientY - bounds.y - 10
            };
            this.$emit('cueCreated', cue);
        },
        createSelectionCue: function(e) {
            if (!this.project.isSample) {
                if (!this.user.active_pay) {
                    if (!this.issrc && !this.project.unlocked) {
                        this.$emit('openDialog', 'no-collaborator-dialog');
                        return;
                    }
                }
            }
            if (!this.currentCueLayer || (this.showCuesLayerIDs !== null && !this.showCuesLayerIDs.includes(this.currentCueLayer.id))) {
                this.$emit('openDialog', 'no-layers-dialog');
                return;
            }
            if (this.locked) return;
            const canvas = this.$el.querySelector('.canvasWrapper canvas');
            const bounds = canvas.getBoundingClientRect();

            let originX;
            let originY;
            let initWidth;
            let initHeight;

            if (e.type === 'touchend' && e.changedTouches.length === 1) {
                originX = Math.min(this.initialX, e.changedTouches[0].clientX);
                originY = Math.min(this.initialY, e.changedTouches[0].clientY);

                initWidth = Math.abs((e.changedTouches[0].clientX - bounds.x - 30) - (this.initialX - bounds.x - 30)) / this.selectedScale;
                initHeight = Math.abs((e.changedTouches[0].clientY - bounds.y - 10) - (this.initialY - bounds.y - 10)) / this.selectedScale;
            } else {                
                originX = Math.min(this.initialX, e.clientX);
                originY = Math.min(this.initialY, e.clientY);

                initWidth = Math.abs((e.clientX - bounds.x - 30) - (this.initialX - bounds.x - 30)) / this.selectedScale;
                initHeight = Math.abs((e.clientY - bounds.y - 10) - (this.initialY - bounds.y - 10)) / this.selectedScale;
            }

            const cue = {
                type: 'selection',
                width: initWidth,
                height: initHeight,
                x: originX - bounds.x,
                y: originY - bounds.y
            };

            this.$emit('cueCreated', cue);
            
            this.cueSelected = false;
        },
        createPositionCue: function(e) {
            if (!this.project.isSample) {
                if (!this.user.active_pay) {
                    if (!this.issrc && !this.project.unlocked) {
                        this.$emit('openDialog', 'no-collaborator-dialog');
                        return;
                    }
                }
            }
            if (!this.currentCueLayer || (this.showCuesLayerIDs !== null && !this.showCuesLayerIDs.includes(this.currentCueLayer.id))) {
                this.$emit('openDialog', 'no-layers-dialog');
                return;
            }
            if (this.locked) return;
            const canvas = this.$el.querySelector('.canvasWrapper canvas');
            const bounds = canvas.getBoundingClientRect();
            const finalLineXPos = (e.clientX - bounds.x);
           
            let finalLineYPos = (e.clientY - bounds.y);
            
            if (finalLineYPos <= 30) {
                finalLineYPos = 15;
            } else {
                finalLineYPos -= (14 * this.selectedScale);
            }
            const cue = {
                type: 'position',
                x: finalLineXPos,
                y: finalLineYPos,
                width: 30,
                height: 30
            };
            this.$emit('cueCreated', cue);
        },
        preventContext: function(e) {
            e.preventDefault();
            e.stopPropagation();
            return false;
        },
        createTextNote: function(e) {
            const canvas = this.$el.querySelector('.canvasWrapper canvas');
            const bounds = canvas.getBoundingClientRect();
            let textXPos = (e.clientX - bounds.x);
            if ((textXPos + (100 * this.selectedScale)) > this.pageWidth) {
                textXPos -= ((textXPos + (100 * this.selectedScale)) - this.pageWidth);
            }
            let textYPos = (e.clientY - bounds.y);
            if ((textYPos + (24 * this.selectedScale)) > this.pageHeight) {
                textYPos -= ((textYPos + (24 * this.selectedScale)) - this.pageHeight);
            }
            const data = {
                x: textXPos / this.pageWidth,
                y: textYPos / this.pageHeight
            };

            this.$emit('addNote', {
                data,
                type: 'text'
            });
        },
        createHighlightNote: function(e) {
            if (!this.project.isSample) {
                if (!this.user.active_pay) {
                    if (!this.issrc && !this.project.unlocked) {
                        this.$emit('openDialog', 'no-collaborator-dialog');
                        return;
                    }
                }
            }
            if (!this.currentNoteLayer || !this.showNotesLayerIDs.includes(this.currentNoteLayer.id)) {
                this.$emit('openDialog', 'no-layers-dialog');
                return;
            }
            if (this.locked) return;
            const canvas = this.$el.querySelector('.canvasWrapper canvas');
            const bounds = canvas.getBoundingClientRect();

            const ctx = canvas.getContext('2d');
            ctx.strokeStyle = 'black';
            ctx.lineWidth = 1;
            ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
            let originX;
            let originY;
            let initWidth;
            let initHeight;

            if (e.type === 'touchend' && e.changedTouches.length === 1) {
                originX = Math.min(this.initialX, e.changedTouches[0].clientX);
                originY = Math.min(this.initialY, e.changedTouches[0].clientY);

                initWidth = Math.abs((e.changedTouches[0].clientX - bounds.x - 30) - (this.initialX - bounds.x - 30)) / this.selectedScale;
                initHeight = Math.abs((e.changedTouches[0].clientY - bounds.y - 10) - (this.initialY - bounds.y - 10)) / this.selectedScale;
            } else {                
                originX = Math.min(this.initialX, e.clientX);
                originY = Math.min(this.initialY, e.clientY);

                initWidth = Math.abs((e.clientX - bounds.x - 30) - (this.initialX - bounds.x - 30)) / this.selectedScale;
                initHeight = Math.abs((e.clientY - bounds.y - 10) - (this.initialY - bounds.y - 10)) / this.selectedScale;
            }
            initWidth = initWidth === 0 ? 100 : initWidth;
            initHeight = initHeight === 0 ? 24 : initHeight;
            this.restoreCanvasState();

            let highlightXPos = (originX - bounds.x);
            if ((highlightXPos + (initWidth * this.selectedScale)) > this.pageWidth) {
                highlightXPos -= ((highlightXPos + (initWidth * this.selectedScale)) - this.pageWidth);
            }
            let highlightYPos = (originY - bounds.y);
            if ((highlightYPos + (initHeight * this.selectedScale)) > this.pageHeight) {
                highlightYPos -= ((highlightYPos + (initHeight * this.selectedScale)) - this.pageHeight);
            }
            const data = {
                width: initWidth,
                height: initHeight,
                x: highlightXPos / this.selectedScale,
                y: highlightYPos / this.selectedScale
            };

            this.$emit('addNote', {
                data,
                type: 'highlight'
            });
        },
        createLineNote: function(e) {
            const canvas = this.$el.querySelector('.canvasWrapper canvas');
            const bounds = canvas.getBoundingClientRect();

            const lineWidth = 300 * this.selectedScale;
            let finalLineXPos = (e.clientX - bounds.x);
            if (((e.clientX - bounds.x) + lineWidth) > this.pageWidth) {
                finalLineXPos -= ((finalLineXPos + lineWidth) - this.pageWidth);
            }

            const data = {
                width: 300,
                height: 2,
                x: finalLineXPos / this.selectedScale,
                y: (e.clientY - bounds.y) / this.selectedScale
            };

            this.$emit('addNote', {
                data,
                type: 'line'
            });
        },
        createStampNote: function(e) {
            const canvas = this.$el.querySelector('.canvasWrapper canvas');
            const bounds = canvas.getBoundingClientRect();            
            const minStamp = (this.currentNoteLayer.stampSize / 2);
            let stampDefualtWidth = (((e.clientX - bounds.x) / this.selectedScale));
            let stampDefualtHeight = (((e.clientY - bounds.y) / this.selectedScale));
            const differnceWidth = (this.pageWidth / this.selectedScale) - stampDefualtWidth;
            const differnceHeight = (this.pageHeight / this.selectedScale) - stampDefualtHeight;
            if (stampDefualtWidth <= minStamp) {
                stampDefualtWidth += (minStamp - stampDefualtWidth) + 10;
            }
            if (stampDefualtHeight <= minStamp) {
                stampDefualtHeight += (minStamp - stampDefualtHeight) + 10;
            }
            if (differnceWidth <= minStamp) {
                if (differnceWidth < 6) {
                    stampDefualtWidth -= minStamp;
                } else if (differnceWidth < 10) {
                    stampDefualtWidth -= (differnceWidth * (this.selectedScale * 3));
                } else if (differnceWidth < 15) {
                    stampDefualtWidth -= (differnceWidth * (this.selectedScale * 1.5));
                } else {
                    stampDefualtWidth -= (differnceWidth);
                }
            }
            if (differnceHeight <= minStamp) {
                if (differnceHeight < 5) {
                    stampDefualtHeight -= minStamp;
                } else if (differnceHeight < 10) {
                    stampDefualtHeight -= (differnceHeight * (this.selectedScale * 2));
                } else if (differnceHeight < 15) {
                    stampDefualtHeight -= (differnceHeight * this.selectedScale);
                } else {
                    stampDefualtHeight -= (differnceHeight);
                }
            }
            const data = {
                width: this.currentNoteLayer.stampSize,
                height: this.currentNoteLayer.stampSize,
                stampSize: this.currentNoteLayer.stampSize,
                x: stampDefualtWidth - minStamp,
                y: stampDefualtHeight - minStamp,
                subType: this.selectedToolIndex
            };

            this.$emit('addNote', {
                data,
                type: 'stamp'
            });
        },
        createImage: function(e) {
            const canvas = this.$el.querySelector('.canvasWrapper canvas');
            const bounds = canvas.getBoundingClientRect();
            let textXPos = (e.clientX - bounds.x);
            if ((textXPos + (120 * this.selectedScale)) > this.pageWidth) {
                textXPos -= ((textXPos + (120 * this.selectedScale)) - this.pageWidth);
            }
            let textYPos = (e.clientY - bounds.y);
            if ((textYPos + (120 * this.selectedScale)) > this.pageHeight) {
                textYPos -= ((textYPos + (120 * this.selectedScale)) - this.pageHeight);
            }
            const data = {
                width: 120,
                height: 120,
                x: textXPos / this.pageWidth,
                y: textYPos / this.pageHeight
            };

            this.$emit('addNote', {
                data,
                type: 'image'
            });
        },
        createCountNote: function(e) {
            const canvas = this.$el.querySelector('.canvasWrapper canvas');
            const bounds = canvas.getBoundingClientRect();

            const data = {
                width: 100,
                height: 24,
                x: e.clientX - bounds.x,
                y: e.clientY - bounds.y,
                subType: this.selectedToolIndex
            };

            switch (this.selectedToolIndex) {
            case '0':
                data.text = '1 2 3 4';
                break;
            case '1':
                data.text = '1 2 3 4 5 6';
                break;
            case '2':
                data.text = '1 2 3 4 5 6 7 8';
                break;
            default:
                break;
            }

            this.$emit('addNote', {
                data,
                type: 'count'
            });
        },
        handleResize() {
            const canvasWrapper = document.querySelector('.canvasWrapper');
            const canvasWidth = canvasWrapper.offsetWidth;
            const canvasSF = canvasWidth / 768;
            const pdfViewer = document.querySelector('.page-viewer');
            pdfViewer.style.setProperty('--scale-factor', canvasSF);
            const qlPageLayouts = document.querySelectorAll('.md-layout.ql-page-layout');
            const qlPageLayoutsArray = Array.from(qlPageLayouts);

            for (const qlPageLayout of qlPageLayoutsArray) {
                const qlPageLayoutHeight = 768;
                const qlPageLayoutWidth = 1024;

                const newWidth = `var(--scale-factor) * ${qlPageLayoutWidth}px`;
                const newHeight = `var(--scale-factor) * ${qlPageLayoutHeight}px`;

                qlPageLayout.style.height = newHeight;
                qlPageLayout.style.width = newWidth;
            }
            const qlPagesInner = document.querySelector('.md-layout.ql-page-layout .ql-pages-inner');
            const qlPageGraphics = document.querySelectorAll('.md-layout.ql-page-layout .ql-pages-inner svg.ql-page-graphics');

            const computedStyle = window.getComputedStyle(qlPagesInner);
            const padding = Number.parseFloat(computedStyle.paddingTop) + Number.parseFloat(computedStyle.paddingBottom);
            const border = Number.parseFloat(computedStyle.borderTopWidth) + Number.parseFloat(computedStyle.borderBottomWidth);
            const margin = Number.parseFloat(computedStyle.marginTop) + Number.parseFloat(computedStyle.marginBottom);

            const qlPagesInnerHeight = qlPagesInner.offsetHeight - padding - border - margin;

            for (const qlPageGraphicsElement of qlPageGraphics) {
                qlPageGraphicsElement.style.height = `${qlPagesInnerHeight}px`;
            }
        },
        handleTouchStart(event) {
            const currentTime = Date.now();
            const timeDifference = currentTime - this.lastTouchTime;

            if (timeDifference < 300 && timeDifference > 0) {
                if (event.touches !== undefined && event.touches.length === 1) {
                    event.preventDefault();
                    event = event.touches[0];
                }
                this.doubleClicked(event);
            }

            this.lastTouchTime = currentTime;
        }
    },
    directives: {
        longpress: {
            bind(el, binding) {
                let timer;
                const longPressDuration = 400; // Duration in milliseconds

                const startLongPress = (event) => {
                    timer = setTimeout(() => {
                        binding.value(event);
                    }, longPressDuration);
                };

                const cancelLongPress = () => {
                    clearTimeout(timer);
                };

                el.addEventListener('touchstart', startLongPress);
                el.addEventListener('touchend', cancelLongPress);
                el.addEventListener('touchcancel', cancelLongPress);
            }
        }
    },
    mounted: function() {
        this.renderPage();
    },
    beforeDestroy() {
    }
};

export default ProjectPdfPage;
</script>

<style lang="scss">
.canvasWrapper.hide-text-layer canvas{
    cursor: crosshair;
}

.textLayer {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  opacity: 0.2;
  line-height: 1.0;
}

.textLayer.hide-text-layer{
    z-index: -1;
}

.textLayer > div {
  color: transparent;
  position: absolute;
  white-space: pre;
  cursor: text;
  -webkit-transform-origin: 0% 0%;
  -moz-transform-origin: 0% 0%;
  -o-transform-origin: 0% 0%;
  -ms-transform-origin: 0% 0%;
  transform-origin: 0% 0%;
}

.textLayer::selection { background: rgb(0,0,255); }
.textLayer::-moz-selection { background: rgb(0,0,255); }
.no-scroll {
    overflow: hidden !important;
}
</style>


// WEBPACK FOOTER //
// pdf-page.vue?4217b399